<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useKeyControlsService } from '@composables/use-key-controls-service'
import { useMediaQuery } from '@composables/use-media-query'
import { MEDIA_DESKTOP_UP } from '@design/breakpoints'
import { useAuthStore } from '@stores/auth'
import { useSearchStore } from '@stores/search'
import { pageKey } from '@utils/page-key'
import SearchField from '@components/search/search-field'
import AccountMenu from './account-menu'

const authStore = useAuthStore()
const searchStore = useSearchStore()
const route = useRoute()
const router = useRouter()
const keyControlsService = useKeyControlsService()
const isDesktop = useMediaQuery(MEDIA_DESKTOP_UP)
const searchField = ref(null)
const searchKeyword = ref('')
const loggedIn = computed(() => authStore.loggedIn)
const routeKeyword = computed(() => route.params?.keyword)

// ⌘/Ctrl+S (key-controls-service) lands here.
function focusSearchField() {
    searchField.value?.focus()
}

// SearchField submits a trimmed keyword; the URL carries it with underscores (as today).
function onSearch(keyword) {
    if (!keyword) return
    searchStore.updateKeyword(keyword)
    router.push({
        name: 'search/results/top',
        params: { keyword: keyword.replace(/\s+/g, '_') },
    })
}

function onNavigate(to) {
    router.push(to)
}

function onClearSearch() {
    searchKeyword.value = ''
}

// Keyword ↔ URL sync. No refocus here (D23): a route change must not move focus or
// open the typeahead — ⌘S and the phone re-tap focus through focusSearch.
watch(() => route.path, () => {
    const keyword = routeKeyword.value
    if (!keyword) {
        searchKeyword.value = ''
        return
    }

    const routeValue = keyword.replace(/_/g, ' ')
    if (searchKeyword.value.trim() !== routeValue.trim()) {
        searchKeyword.value = routeValue
    }
    searchStore.updateKeyword(routeValue)
}, { immediate: true })

watch(() => pageKey(route), () => searchField.value?.blur())

onMounted(() => keyControlsService.on('focusSearch', focusSearchField))
onBeforeUnmount(() => keyControlsService.off('focusSearch', focusSearchField))
</script>

<template>
    <header
        v-if="route.name !== 'login'"
        class="top-bar"
    >
        <div class="top-bar-inner">
            <div class="top-bar-search">
                <SearchField
                    ref="searchField"
                    v-model="searchKeyword"
                    :layout="isDesktop ? 'desktop' : 'tablet'"
                    launcher
                    :keys="isDesktop"
                    typeahead
                    @submit="onSearch"
                    @clear="onClearSearch"
                    @navigate="onNavigate"
                />
            </div>

            <div class="top-bar-account">
                <div
                    v-if="!loggedIn"
                    class="auth-buttons"
                >
                    <!-- A handler keeps BaseButton's own hover/press states: a handler-less BaseButton is a pass-through label with pointer-events: none. Not inside a form, so the submit trap doesn't apply. -->
                    <BaseLink
                        class="auth-link"
                        name="login"
                    >
                        <BaseButton
                            variant="tertiary"
                            size="m"
                            @click="() => {}"
                        >Log In</BaseButton>
                    </BaseLink>
                    <BaseLink
                        class="auth-link"
                        name="signup"
                    >
                        <BaseButton
                            variant="primary"
                            size="m"
                            @click="() => {}"
                        >Sign Up</BaseButton>
                    </BaseLink>
                </div>
                <div
                    v-else
                    class="user-details"
                >
                    <AccountMenu />
                </div>
            </div>
        </div>
    </header>
</template>

<style lang="scss" scoped>
@use '@design' as *;

// The bar is full-bleed chrome; its contents share the page-section box (BaseSection's
// container + side padding) so search and account line up with page content at every width.
// The search matches production: flex 1, capped at 600.
.top-bar {
    height: $size-top-bar-height;
    background: $c-surface-chrome;
}

.top-bar-inner {
    display: flex;
    gap: $space-24;
    align-items: center;
    justify-content: space-between;
    height: 100%;

    @include container;
    @include sidePadding;
}

.top-bar-search {
    flex: 1;
    min-width: 0;
    max-width: 600px;
}

.top-bar-account {
    display: flex;
    flex-shrink: 0;
    align-items: center;
}

.auth-buttons {
    display: flex;
    gap: $space-8;
    align-items: center;
}

// The link is the navigation target and must be pill-sized (#354); the radius lets the
// global focus outline follow the pill, and no underline in any link state.
.auth-link {
    display: inline-flex;
    text-decoration: none;
    border-radius: $radius-full;
}

.user-details {
    display: flex;
    align-items: center;
}
</style>
